import type { Deployment } from '../../types/backend';
import { DeploymentStatus, DriftReason, DriftStatus, ReconcileBlockedReason } from '../../types/backend';
import type { ReconcileBlockedReasonType } from '../../types/backend';
import type { ServiceComponentSummary } from './services/catalogService';
import type { DependencyInfo } from './hooks/useServiceComponentDependencies';

export type DerivedState =
  | 'missing-params'
  | 'pending-approval'
  | 'drifted'
  | 'rescue-required'
  | 'acknowledged-drift'
  | 'initial-subscription'
  | 'reconciling'
  | 'waiting-on-dependency'
  | 'unsubscribed'
  | 'deregistering'
  | 'terminating'
  | 'awaiting-heartbeat'
  | 'awaiting-bootstrap'
  | 'in-sync'
  | 'unknown';

export type RowSeverity = 'err' | 'warn' | 'info' | 'ok' | 'muted';

/** Derived states that surface under the "Needs action" filter. */
export const ATTENTION_STATES: DerivedState[] = [
  'missing-params', 'pending-approval', 'drifted', 'rescue-required', 'awaiting-heartbeat', 'unknown',
];

/** Derived states that represent in-flight reconciliation. */
export const RECONCILING_STATES: DerivedState[] = [
  'initial-subscription', 'reconciling', 'waiting-on-dependency', 'terminating', 'deregistering',
];

/** Reconcile-blocked reasons that mean the row is held behind a dependency. */
const DEPENDENCY_BLOCKS: ReconcileBlockedReasonType[] = [
  ReconcileBlockedReason.DEP_NOT_IN_SYNC,
  ReconcileBlockedReason.DEP_ORDER_UNRESOLVED,
  ReconcileBlockedReason.DEP_PRODUCT_NOT_FOUND,
  ReconcileBlockedReason.DEP_OUTPUT_UNAVAILABLE,
];

function blockedOnDependency(d: Deployment): boolean {
  return !!d.reconcile_blocked_reason && DEPENDENCY_BLOCKS.includes(d.reconcile_blocked_reason);
}

export type DefaultTab = 'overview' | 'params' | 'history';

export type MetaToken = { text: string } | { who: string };

export interface RowIdentity {
  product: string;
  productDeprecated: boolean;
  account: string;
  region: string;
  version: string | null;
}

export interface RowPill {
  label: string;
  severity: RowSeverity;
}

export interface RowConfig {
  deploymentId: string;
  state: DerivedState;
  severity: RowSeverity;
  pill: RowPill;
  defaultTab: DefaultTab;
  metaStrip: MetaToken[];
  identity: RowIdentity;
}

function stateFor(d: Deployment, product?: ServiceComponentSummary): DerivedState {
  if (d.drift_reason) {
    switch (d.drift_reason) {
      case DriftReason.MISSING_REQUIRED_PARAMETERS: return 'missing-params';
      case DriftReason.PENDING_USER_APPROVAL: return 'pending-approval';
      case DriftReason.MANUAL_CHANGE_IN_AWS: return 'drifted';
      case DriftReason.RECONCILIATION_FAILED: return 'rescue-required';
      case DriftReason.ACKNOWLEDGED_ERROR: return 'acknowledged-drift';
      case DriftReason.INITIAL_SUBSCRIPTION: return 'initial-subscription';
      case DriftReason.USER_UPDATED_DESIRED:
        return blockedOnDependency(d) ? 'waiting-on-dependency' : 'reconciling';
      default: return 'unknown';
    }
  }
  switch (d.drift_status) {
    case DriftStatus.IN_SYNC:
      // Deregister settled (desired-state param deleted, sync SYNCED): OpsPilot
      // no longer manages this product, but the stack itself keeps running —
      // distinct from a normal actively-managed in-sync row.
      return d.desired_status === DeploymentStatus.UNSUBSCRIBED ? 'unsubscribed' : 'in-sync';
    case DriftStatus.PENDING_DEREGISTER: return 'deregistering';
    case DriftStatus.PENDING_TERMINATION: return 'terminating';
    case DriftStatus.UNKNOWN: return 'awaiting-heartbeat';
    case DriftStatus.MISSING:
      return product?.provisioning_type === 'manual' ? 'awaiting-bootstrap' : 'awaiting-heartbeat';
    case DriftStatus.FAILED: return 'rescue-required';
    case DriftStatus.OUTDATED:
      return blockedOnDependency(d) ? 'waiting-on-dependency' : 'reconciling';
    default: return 'unknown';
  }
}

const SEVERITY_OF: Record<DerivedState, RowSeverity> = {
  'missing-params': 'err',
  'pending-approval': 'warn',
  'drifted': 'warn',
  'rescue-required': 'err',
  'acknowledged-drift': 'muted',
  'initial-subscription': 'info',
  'reconciling': 'info',
  'waiting-on-dependency': 'info',
  'unsubscribed': 'muted',
  'deregistering': 'info',
  'terminating': 'info',
  'awaiting-heartbeat': 'warn',
  'awaiting-bootstrap': 'info',
  'in-sync': 'ok',
  'unknown': 'warn',
};

const PILL_OF: Record<DerivedState, string> = {
  'missing-params': 'Missing parameters',
  'pending-approval': 'Pending approval',
  'drifted': 'Drifted in AWS',
  'rescue-required': 'Rescue required',
  'acknowledged-drift': 'Acknowledged (drift)',
  'initial-subscription': 'Initial subscription',
  'reconciling': 'Reconciling',
  'waiting-on-dependency': 'Waiting on dependency',
  'unsubscribed': 'Unsubscribed',
  'deregistering': 'Deregistering',
  'terminating': 'Terminating',
  'awaiting-heartbeat': 'Awaiting heartbeat',
  'awaiting-bootstrap': 'Awaiting bootstrap',
  'in-sync': 'In sync',
  'unknown': 'Unknown state',
};

const DEFAULT_TAB_OF: Record<DerivedState, DefaultTab> = {
  'missing-params': 'params',
  'pending-approval': 'overview',
  'drifted': 'overview',
  'rescue-required': 'overview',
  'acknowledged-drift': 'overview',
  'initial-subscription': 'overview',
  'reconciling': 'overview',
  'waiting-on-dependency': 'overview',
  'unsubscribed': 'overview',
  'deregistering': 'overview',
  'terminating': 'overview',
  'awaiting-heartbeat': 'overview',
  'awaiting-bootstrap': 'overview',
  'in-sync': 'overview',
  'unknown': 'overview',
};

function metaStripFor(
  d: Deployment,
  state: DerivedState,
  dependencies: DependencyInfo[] | undefined,
): MetaToken[] {
  const unmet = (dependencies ?? []).filter((x) => !x.met);

  switch (state) {
    case 'missing-params': {
      const tokens: MetaToken[] = [];
      if (unmet.length > 0) {
        tokens.push({ text: 'Blocked by' });
        unmet.forEach((u, i) => {
          if (i > 0) tokens.push({ text: ',' });
          tokens.push({ who: u.productName });
        });
      }
      if (!d.actual_version) {
        if (tokens.length) tokens.push({ text: '·' });
        tokens.push({ text: 'Never deployed' });
      }
      if (d.drift_reason_detail) {
        if (tokens.length) tokens.push({ text: '·' });
        tokens.push({ text: d.drift_reason_detail });
      }
      return tokens.length ? tokens : [{ text: 'Required parameters unresolved' }];
    }
    case 'pending-approval':
      return [{ text: 'Ready to launch — awaiting operator approval' }];
    case 'drifted':
      return [{ text: 'Drift detected in AWS — one or more fields diverged' }];
    case 'rescue-required': {
      const tokens: MetaToken[] = [{ text: 'Reconciler failed and escalated' }];
      if (d.rescue_annotation) {
        tokens.push({ text: '·' });
        tokens.push({ text: d.rescue_annotation });
      }
      return tokens;
    }
    case 'acknowledged-drift':
      return [{ text: 'Drift acknowledged — suppressed from Needs action' }];
    case 'initial-subscription':
      return [{ text: 'Just subscribed — reconciler picks it up on its next tick' }];
    case 'reconciling':
      return [{ text: 'Desired changed — reconciler working' }];
    case 'waiting-on-dependency': {
      const dep = d.reconcile_blocking_dep;
      return [{ text: dep ? `Waiting on dependency: ${dep}` : 'Waiting on a dependency to converge' }];
    }
    case 'unsubscribed':
      return [{ text: 'OpsPilot stopped managing this — the product is still running' }];
    case 'deregistering':
      return [{ text: 'Deregistering — deleting desired state, product stays running' }];
    case 'terminating':
      return [{ text: 'Terminate in flight' }];
    case 'awaiting-heartbeat':
      return [{ text: 'No heartbeat since desired state was set' }];
    case 'awaiting-bootstrap':
      return [{ text: 'Bootstrap product not yet provisioned in this region' }];
    case 'in-sync':
      return [{ text: 'Heartbeat healthy' }];
    case 'unknown':
    default:
      return [{ text: 'Unknown state — see history and drift detail' }];
  }
}

export function rowConfigFor(
  deployment: Deployment,
  product: ServiceComponentSummary | undefined,
  dependencies: DependencyInfo[] | undefined,
): RowConfig {
  const state = stateFor(deployment, product);
  const severity = SEVERITY_OF[state];
  return {
    deploymentId: deployment.id,
    state,
    severity,
    pill: { label: PILL_OF[state], severity },
    defaultTab: DEFAULT_TAB_OF[state],
    metaStrip: metaStripFor(deployment, state, dependencies),
    identity: {
      product: deployment.product_name ?? deployment.product_id,
      productDeprecated:
        product?.status === 'deprecated' || product?.status === 'archived',
      account: deployment.account_id,
      region: deployment.region,
      version: deployment.desired_version ?? null,
    },
  };
}
